import { gql, useMutation } from "@apollo/client";
import {
  IconButton,
  SideSheet,
  Table,
  CrossIcon,
  TextInputField,
  toaster,
  Button,
} from "evergreen-ui";
import React, { useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import DragZone from "../DragZone";
import FileUploading from "../FileUploading";
import GalleryBox from "../GalleryBox";

interface IProps extends RouteComponentProps<any> {
  gallery: any;
  refetch: any;
}

const INSERT_IMAGE = gql`
  mutation newImage($object: gallery_image_insert_input!) {
    insert_gallery_image_one(object: $object) {
      id
      url
      name
      collection
      type
      created_at
      sequence
    }
  }
`;

const UPDATE_GALLERY = gql`
  mutation updateGallery($id: Int!, $set: gallery_collection_set_input!) {
    update_gallery_collection_by_pk(pk_columns: { id: $id }, _set: $set) {
      id
      name
      sequence
      __typename
      gallery_images(order_by: { sequence: desc }) {
        id
        url
        name
        collection
        type
        created_at
        sequence
        __typename
      }
    }
  }
`;

const DELETE_GALLERY = gql`
  mutation deleteGallery($id: Int!) {
    delete_gallery_collection(where: { id: { _eq: $id } }) {
      returning {
        id
      }
    }
  }
`;
const AdminGalleryRow: React.FC<IProps> = ({ gallery, refetch }) => {
  const [showSide, setShowSide] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<any>([]);
  const [newImage] = useMutation(INSERT_IMAGE);
  const [updateGallery, { loading }] = useMutation(UPDATE_GALLERY);
  const [deleteGallery, { loading: deleteLoading }] = useMutation(
    DELETE_GALLERY
  );
  const [galleryData, setGalleryData] = useState({
    name: gallery.name,
    sequence: gallery.sequence,
  });
  const toggleSide = () => {
    setShowSide(!showSide);
  };

  const onFilesUpload = (files: any) => {
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const removeFileFromList = (file) => {
    let fileIndex = selectedFiles.findIndex((a) => a.name === file.name);
    console.log(fileIndex);
    let list = selectedFiles;
    list.splice(fileIndex, 1);
    setSelectedFiles(list);
  };

  const onFileComplete = (url: string, index: number, file: any) => {
    newImage({
      variables: {
        object: {
          name: file.name,
          collection: gallery.id,
          url,
          type: file.type,
        },
      },
    })
      .then(() => {
        toaster.success("Image uploaded!");
        refetch();
        removeFileFromList(file);
      })
      .catch((e) => {
        toaster.danger(e.message);
      });
  };

  const submitUpdateGallery = (e) => {
    e.preventDefault();
    updateGallery({
      variables: {
        id: gallery.id,
        set: {
          name: galleryData.name,
          sequence: galleryData.sequence,
        },
      },
    })
      .then(() => {
        toaster.success("Changes saved!");
      })
      .catch((e) => {
        toaster.danger(e.message);
      });
  };

  const onDeleteGallery = (e) => {
    e.preventDefault();
    deleteGallery({
      variables: {
        id: gallery.id,
      },
    })
      .then(() => {
        refetch(() => {
          toaster.success("Album deleted!");
        });
      })
      .catch((e) => {
        toaster.danger(e.message);
      });
  };
  return (
    <>
      <Table.Row
        onClick={toggleSide}
        className=" hover:bg-gray-100 cursor-pointer py-2"
      >
        <Table.TextCell>
          {gallery.gallery_images.length > 0 ? (
            <p className=" flex flew-row items-center">
              <span>
                <img
                  className=" box-60 object-contain"
                  alt={gallery.name}
                  src={gallery.gallery_images[0].url}
                />
              </span>
              and {gallery.gallery_images.length - 1} more
            </p>
          ) : (
            "No files yet."
          )}{" "}
        </Table.TextCell>
        <Table.TextCell>{gallery.name}</Table.TextCell>
        <Table.TextCell>{gallery.sequence}</Table.TextCell>
      </Table.Row>
      <SideSheet
        isShown={showSide}
        containerProps={{
          display: "flex",
          flex: "1",
          flexDirection: "column",
          maxWidth: "100vw",
        }}
        onCloseComplete={() => {
          setShowSide(false);
        }}
      >
        <div className=" relative w-full min-h-full">
          <div className=" w-full p-3 bg-white flex flex-row justify-between">
            <h1 className=" font-semibold text-lg">{gallery.name}</h1>
            <IconButton icon={CrossIcon} onClick={toggleSide} />
          </div>
          <form
            onSubmit={submitUpdateGallery}
            className=" w-full p-3 bg-gray-50"
          >
            <TextInputField
              value={galleryData.name}
              label="Album Name"
              onChange={(e) => {
                setGalleryData({ ...galleryData.name, name: e.target.valye });
              }}
            />
            <TextInputField
              value={galleryData.sequence}
              label="Sequence"
              onChange={(e) => {
                if (!isNaN(e.target.value)) {
                  setGalleryData({
                    ...galleryData.name,
                    sequence: e.target.value,
                  });
                }
              }}
            />
            <div>
              <Button
                disabled={loading}
                isLoading={loading}
                onClick={submitUpdateGallery}
                appearance="primary"
                className=" mr-4"
              >
                Save
              </Button>
              <Button
                disabled={deleteLoading}
                isLoading={deleteLoading}
                onClick={onDeleteGallery}
                appearance="primary"
                intent="danger"
              >
                Delete
              </Button>
            </div>
          </form>
          {selectedFiles.map((fil: any, index) => (
            <FileUploading
              file={fil}
              key={index}
              index={index}
              removeFile={removeFileFromList}
              onComplete={onFileComplete}
            />
          ))}
          {gallery.gallery_images.length > 0 ? (
            <div className="w-full mx-auto grid grid-cols-2 md:grid-cols-3 row-gap-1 col-gap-1 pb-1">
              {gallery.gallery_images.map((img, index) => (
                <GalleryBox img={img} key={index} refetch={refetch} />
              ))}
            </div>
          ) : (
            <div className=" w-full p-4 flex flex-col items-center justify-center">
              <p className=" mb-4">No files found.</p>
            </div>
          )}
          <div className=" sticky bottom-0 mt-96 w-full p-2 bg-white shadow-2xl border-t border-solid border-gray-100 z-20">
            <DragZone
              selectedFiles={selectedFiles}
              handleFiles={onFilesUpload}
            />
          </div>
        </div>
      </SideSheet>
    </>
  );
};

export default withRouter(AdminGalleryRow);
